import { Nullable } from '../common/Nullable'
import { UserConsentState } from '../components/Consent/model'
import { MeetingLanguage } from './Room'
import { BusinessEventType } from './BusinessEvent'

export type AnonymousAccessType = 'allow' | 'deny'
export type ConsentUploadSetting = 'required' | 'optional'

export type Account = {
  name: string
  tenantType: string
  parentId?: string
  config: AccountConfig
  enterpriseConfig: EnterpriseAccountConfig
  effectiveConfig: EffectiveAccountConfig
  createdAt: string
  updatedAt: string
  metadata?: { [key: string]: unknown }
}

export const getAccountCRMIntegrationConfig = (account: Account | undefined, serviceId: string): CRMAccountIntegrationConfig | null => {
  const config = account?.config.integrations.find((config) => config.type === 'crm' && config.serviceId === serviceId) ?? null
  return config ? (config as CRMAccountIntegrationConfig) : null
}

export type LogoConfig = {
  logoFile: FileInfo
}

export type AccountConfig = {
  anonymousAccess?: AnonymousAccessType
  integrations: AccountIntegrationConfig[]
  restrictedSessionDelete: boolean
  complianceNotice?: ComplianceNotice
  aiMeetingTemplateCalculator: boolean
  aiMeetingTitleCalculator: boolean
  userConsentTrackingEnabled: boolean
  userConsentManualOverride: boolean
  consentApprovalCheck: boolean
  userConsentManualConfigs: UserConsentManualConfigs
  piiRedactionCategories: string[]
  piiRedactionEntities: string[]
  sentimentAnalysis: boolean
  clientProfileOnDemandCRMExecution: boolean
  assistantMeetings: boolean
  email: EmailConfig
  exportConfirmation: ExportConfirmationConfig
  meetingScorecard: boolean
  crossAccountAccess: boolean
  inviteLinkAutoRenewal: boolean
  crossClientQueryUserExecution: boolean
  assets?: {
    logo?: LogoConfig
  }
  privateMeetings: boolean
  multiLanguageSupport: MultiLanguageSupportConfig
  meetingPrepEmail: MeetingPrepEmailConfig
  meetingProcessedDoneEmail: MeetingProcessedDoneEmailConfig
  crossClientEvaluationEmail: CrossClientEvaluationEmailConfig
  businessEvent: boolean
  emailBusinessEvent: boolean
  businessEventExport: boolean
  integrationExportComplianceMessage: IntegrationExportComplianceMessageConfig
  emailComplianceMessage: EmailComplianceMessageConfig
  pdfComplianceMessage: PdfComplianceMessageConfig
  copyComplianceMessage: CopyComplianceMessageConfig
  restrictedSessionTrimming: boolean
}

export type AccountUpdateOptions = {
  changeExistingPrivateMeetingsToPublic?: boolean
}

export type ExportConfirmationConfig = {
  integrationEnabled: boolean
  documentEnabled: boolean
  message: string
}

export type IntegrationExportComplianceMessageConfig = {
  message: string
  enabled: boolean
}

export type EmailComplianceMessageConfig = {
  message: string
  enabled: boolean
}

export const LOCK_USER_OVERRIDE = 'lock_user_override'
export const LOCK_ALL_OVERRIDE = 'lock_all_override'

export type PdfComplianceMessageConfig = {
  message: string
  enabled: boolean
}

export type CopyComplianceMessageConfig = {
  message: string
  enabled: boolean
}

export type EnterpriseAccountConfig = {
  integrationsConfig: IntegrationsEnterpriseAccountConfig
  oauthAppsConfig: OAuthAppsEnterpriseAccountConfig
  meetingSummaryConfig: MeetingSummaryEnterpriseAccountConfig
  complianceConfig: ComplianceAccountConfig
  piiRedactionCategories?: string[]
  piiRedactionEntities?: string[]
  restrictedSessionDelete?: boolean
  restrictedSessionDeleteGlobal?: boolean
  restrictedSessionTrimming?: boolean
  downloadTranscriptAvailable: boolean
  crossAccountAccess?: boolean
  downstreamDashboards: boolean
  sentimentAnalysis?: boolean
  privateMeetings?: boolean
  assistantMeetings?: boolean
  integrations?: AccountIntegrationConfig[]
  multiLanguageSupport?: MultiLanguageSupportConfig
  businessEvent?: boolean
  emailBusinessEvent?: boolean
  businessEventExport?: boolean
  meetingProcessedDoneEmail?: MeetingProcessedDoneEmailConfig
  meetingPrepEmail?: MeetingPrepEmailConfig
  meetingScorecard?: boolean
  aiMeetingTitleCalculator?: boolean
  lockedIPHierarchyTypes?: LockedIPHierarchyType[]
  assets?: {
    logo?: LogoConfig
  }
  lockTemplates: boolean
  lockMeetingTemplates: boolean
  lockDocumentTypes: boolean
  lockTemplateOverride?: typeof LOCK_USER_OVERRIDE | typeof LOCK_ALL_OVERRIDE
  billing: BillingConfig
}

export type LockedIPHierarchyType = {
  type: string
  typeId: string
}

export type IntegrationsEnterpriseAccountConfig = {
  restrictions?: IntegrationRestrictions
  integrationSetupConfigs?: IntegrationSetupConfig[]
}

export type OAuthAppsRestrictions = {
  restrictedOAuthApps: string[]
}

export type OAuthAppsEnterpriseAccountConfig = {
  restrictions?: OAuthAppsRestrictions
}

export type IntegrationRestrictions = {
  allowedIntegrations?: string[]
  deniedIntegrations: string[]
}

export type IntegrationSetupConfig = {
  integration?: string
  url?: string
  openInNewTab?: boolean
}

export type MeetingSummaryEnterpriseAccountConfig = {
  disableComments: boolean
}

export type ComplianceAccountConfig = {
  complianceNotice?: ComplianceNotice
  userConsentTrackingEnabled?: boolean
  userConsentManualOverride?: boolean
  consentApprovalCheck?: boolean
  exportConfirmation?: ExportConfirmationConfig
  integrationExportComplianceMessage?: IntegrationExportComplianceMessageConfig
  emailComplianceMessage?: EmailComplianceMessageConfig
  pdfComplianceMessage?: PdfComplianceMessageConfig
  copyComplianceMessage?: CopyComplianceMessageConfig
  userConsentManualConfigs: UserConsentManualConfigs
}

export type ConsentWarningTextConfig = {
  enabled: boolean
  text: string
}

export type EvidenceUploadConfig = {
  enabled: boolean
  setting: ConsentUploadSetting
}
export type CommentUploadConfig = {
  enabled: boolean
  setting: ConsentUploadSetting
}

export type UserConsentManualConfigs = {
  consentWarningText: ConsentWarningTextConfig
  evidenceUpload: EvidenceUploadConfig
  commentUpload: CommentUploadConfig
  meetingLevelConsent: boolean
}

export type AuditLogConfig = {
  enabled: boolean
}

export type BillingConfig = {
  featureAdvertisementEnabled: boolean
}

export type EffectiveAccountConfig = {
  complianceConfig: ComplianceAccountConfig
  auditLogConfigs?: AuditLogConfig
  piiRedactionCategories: string[]
  piiRedactionEntities: string[]
  restrictedSessionDelete: boolean
  restrictedSessionDeleteGlobal: boolean
  restrictedSessionTrimming: boolean
  restrictedSessionTrimmingGlobal: boolean
  downloadTranscriptAvailable: boolean
  crossAccountAccess: boolean
  downstreamDashboards: boolean
  sentimentAnalysis: boolean
  crossClientQueryUserExecution: boolean
  privateMeetings: boolean
  assistantMeetings: boolean
  integrations?: AccountIntegrationConfig[]
  multiLanguageSupport: MultiLanguageSupportConfig
  businessEvent: boolean
  emailBusinessEvent: boolean
  businessEventExport: boolean
  meetingProcessedDoneEmail: MeetingProcessedDoneEmailConfig
  meetingPrepEmail: MeetingPrepEmailConfig
  crossClientEvaluationEmail: CrossClientEvaluationEmailConfig
  meetingScorecard?: boolean
  aiMeetingTitleCalculator: boolean
  assets?: {
    logo?: LogoConfig
  }
  billing: BillingConfig
}

export enum AccountIntegrationConfigMappingType {
  SESSION = 'session',
  NOTE = 'note',
  RESULT = 'result',
  CONTACT = 'contact',
  DOCUMENT_SUMMARY = 'documentSummary',
  TASK = 'task',
  ADDITIONAL_OBJECTS = 'additionalObjects',
  BUSINESS_EVENTS = 'businessEvents',
}

export enum DescriptorType {
  FIELD = 'field',
  VARIABLE = 'variable',
  STATIC = 'static',
}

export enum FieldUpdateMode {
  APPEND = 'append',
  PREPEND = 'prepend',
  OVERWRITE = 'overwrite',
  CREATE_ONLY = 'create_only',
}

export type Condition = {
  subject: ConditionValue
  operator: FilterOperator
  source?: ConditionValue | null
}

export type ConditionGroup = {
  operator?: GroupOperator | null
  conditions?: Condition[]
  returnValue?: ConditionValue | null
  fallback?: ConditionGroup | ConditionValue | null
}

export type FieldDescriptor = {
  value?: string | null
  type: DescriptorType
  valueType?: string | null
  valueFormat?: string | null
  updateMode?: FieldUpdateMode | null
  conditions?: ConditionGroup | null
  defaultValue?: string | null
}

export type RelationshipBase = {
  object: string
  field: string
}

export enum GroupOperator {
  AND = 'and',
  OR = 'or',
}

export enum FilterOperator {
  EQ = 'eq',
  NE = 'ne',
  GT = 'gt',
  LT = 'lt',
  GE = 'ge',
  LE = 'le',
  IN = 'in',
}

export const OPERATOR_OPTIONS = [
  { id: 'eq', label: 'Equals' },
  { id: 'ne', label: 'Not Equals' },
  { id: 'gt', label: 'Greater Than' },
  { id: 'lt', label: 'Less Than' },
  { id: 'ge', label: 'Greater or Equal' },
  { id: 'le', label: 'Less or Equal' },
  { id: 'in', label: 'In' },
] as const

export type ConditionValue = {
  value?: string | null
  type: DescriptorType
  object?: string | null
  field?: string | null
  valueType?: string | null
  valueFormat?: string | null
}

export type FilterExpression = {
  field: string
  operator: FilterOperator
  source: ConditionValue
  valueType?: string | null
  valueFormat?: string | null
}

export type Filter = {
  groupOperator: GroupOperator
  expressions: FilterExpression[]
}

export type ContactSync = {
  filters?: Filter[] | null
  applyOwnerFilter?: boolean | null
}

export enum Direction {
  ASC = 'ASC',
  DESC = 'DESC',
}

export type OrderBy = {
  field: string
  direction: Direction
}

export type OrderBySchema = {
  field: string
  direction: Direction
}

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  FETCH = 'fetch',
}

export type RelatedObject = {
  include?: boolean
  dependency?: string[]
  limit?: number | null
  outputFields?: { [key: string]: string }[]
  orderBy?: OrderBy | null
  filters?: Filter[] | null
  fields?: { [key: string]: FieldDescriptor[] } | null
  operationType?: OperationType
  isHousehold?: boolean
  primaryIdAttribute?: string | null
}

export type Relationship = {
  object: string
  field: string
}

export type RelationshipTarget = Relationship & {
  valueType?: string | null
  valueFormat?: string | null
}

export type RelationshipSource = Relationship & {
  filters?: Filter[] | null
}

export type ContactLink = {
  source: RelationshipSource
  target: RelationshipTarget
}

export enum MappingType {
  PREDEFINED = 'PREDEFINED',
  CUSTOM = 'CUSTOM',
}

export type FieldConstraint = {
  maxLength?: number | null
  valueType?: string | null
  valueFormat?: string | null
}

export type UpdateAdditionalInformation = {
  title?: string
  idKey?: string
}

export type DetailedCRMAccountIntegrationMapping = {
  objectName: string
  displayName?: string | null
  fields?: { [key: string]: FieldDescriptor[] }
  additionalInformation?: { [key: string]: unknown }
  filters?: Filter[]
  sync?: ContactSync
  relatedObjects?: { [key: string]: RelatedObject }
  operationType?: OperationType
  contactLinks?: ContactLink[]
  mappingType?: MappingType
  constraints?: { [key: string]: FieldConstraint }
  limit?: number | null
  isHousehold?: boolean | null
}

export type CRMAccountIntegrationConfig = {
  type: 'crm'
  serviceId: string
  loginUrl: string | null
  mappings: IntegrationMappings
  isCustom?: boolean | null
  enableNoteExport: boolean
  enableFullMeetingExport: boolean
  isSyncDisabled?: boolean
}

export type IntegrationMappings = {
  [AccountIntegrationConfigMappingType.CONTACT]?: DetailedCRMAccountIntegrationMapping | null
  [AccountIntegrationConfigMappingType.SESSION]?: DetailedCRMAccountIntegrationMapping | null
  [AccountIntegrationConfigMappingType.TASK]?: DetailedCRMAccountIntegrationMapping | null
  [AccountIntegrationConfigMappingType.NOTE]?: DetailedCRMAccountIntegrationMapping | null
  [AccountIntegrationConfigMappingType.RESULT]?: DetailedCRMAccountIntegrationMapping | null
  [AccountIntegrationConfigMappingType.DOCUMENT_SUMMARY]?: DetailedCRMAccountIntegrationMapping | null
  [AccountIntegrationConfigMappingType.ADDITIONAL_OBJECTS]?: DetailedCRMAccountIntegrationMapping[] | null
  [AccountIntegrationConfigMappingType.BUSINESS_EVENTS]?: Record<BusinessEventType, DetailedCRMAccountIntegrationMapping>
}

export type MeetingProviderAccountIntegrationConfig = {
  type: 'meeting_provider'
  serviceId: string
  sharedPhoneLineEnabled?: boolean | null
  allowedExternalIds: string[]
  defaultProvider: string | null
  defaultLocation: string | null
  adhocAutoJoinEnabled?: boolean | null
}

export type AccountIntegrationConfig = CRMAccountIntegrationConfig | MeetingProviderAccountIntegrationConfig

export type AccountUpdatePayload = {
  name?: string
  config?: {
    anonymousAccess: AnonymousAccessType
  }
}

export interface FileInfo {
  fileId: string
  fileName: string
  fileSize: number
}

export interface ComplianceFileInfo {
  fileId: string
  fileName: string
  fileSize: number
}

export interface ComplianceNotice {
  audioEnabled: boolean
  imageEnabled: boolean
  chatEnabled: boolean
  audioFileOriginal: Nullable<ComplianceFileInfo>
  imageFileOriginal: Nullable<ComplianceFileInfo>
  audioFileConverted: Nullable<ComplianceFileInfo>
  imageFileConverted: Nullable<ComplianceFileInfo>
  chatMessage: string
  imageOverrideEnabled?: boolean | null
  audioPlayOnceOnlyEnabled?: boolean | null
}

export type EmailAction = 'generateReply' | 'pushToDraft' | 'doNotReply'

export interface EmailConfig {
  calculateEmailTypeForConsent: UserConsentState[]
  emailActionsByEmailType: { [key: string]: EmailAction }
  uncategorizedEmailAction: EmailAction
  userRolesByEmailType: { [key: string]: string[] }
}

export interface MultiLanguageSupportConfig {
  enabled: boolean
  defaultLanguage: MeetingLanguage
  extraLanguages: MeetingLanguage[]
  supportedLanguages: MeetingLanguage[]
}

export interface MeetingPrepEmailNotificationConfig {
  meetingOwner: boolean
  teamMemberIds: string[]
}

export interface MeetingPrepEmailConfig {
  enabled: boolean
  includeInfo: 'linkOnly' | 'linkAndSummary'
  notifications: MeetingPrepEmailNotificationConfig
}

export interface MeetingProcessedDoneEmailNotificationConfig {
  teamMemberIds: string[]
}

export interface MeetingProcessedDoneEmailConfig {
  includeInfo: 'linkOnly' | 'linkAndMeetingInfo' | 'linkAndMeetingInfoAndSummary'
  notifications: MeetingProcessedDoneEmailNotificationConfig
}

export interface CrossClientEvaluationEmailNotificationConfig {
  teamMemberIds: string[]
}

export interface CrossClientEvaluationEmailConfig {
  enabled: boolean
  notifications: CrossClientEvaluationEmailNotificationConfig
}
